/**
 * Shared pinned inspector panel (FR-TS-123 / FR-TS-129). Owns `[data-inspector]` and a single
 * current selection — either a feed row (its redacted request/response) or a multi-step flow
 * (its live-updating step breakdown). Both the feed and the Flows panel pin into it; the flow
 * view refreshes in place as the flow advances. Keeps the bearer token redacted.
 */

interface RequestTrace {
  method: string;
  url: string;
  requestHeaders: Record<string, string>;
  requestBody: unknown;
  responseStatus: number | null;
  responseBody: unknown;
}
export interface FeedRow {
  seq: number;
  description: string;
  detail: string;
  correlationId: string;
  trace: RequestTrace | null;
}
export interface FlowStep {
  name: string;
  status: string;
  detail?: string;
  msgType?: string;
  /** Correlation ID of the real call that advanced this step — reconcilable in the bank's audit. */
  correlationId?: string;
}
export interface FlowRef {
  /** The backend's own id for this lifecycle, not a simulator-local counter. */
  id: string;
  kind: string;
  title: string;
  status: string;
  steps: FlowStep[];
}

const STEP_GLYPH: Record<string, string> = { done: '●', active: '◐', pending: '○', failed: '✕', blocked: '⊘' };

type Selection = { kind: 'feed'; seq: number } | { kind: 'flow'; id: string } | null;
let current: Selection = null;

function pretty(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'string') return v;
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}

function span(cls: string, text: string): HTMLSpanElement {
  const s = document.createElement('span');
  s.className = cls;
  s.textContent = text;
  return s;
}

function panel(doc: Document): Element | null {
  return doc.querySelector('[data-inspector]');
}

export function selectedSeq(): number | null {
  return current?.kind === 'feed' ? current.seq : null;
}
export function selectedFlowId(): string | null {
  return current?.kind === 'flow' ? current.id : null;
}

export function renderEmpty(doc: Document): void {
  const p = panel(doc);
  if (p) p.replaceChildren(span('inspector-empty', 'Click a request or a flow to pin its details here.'));
  const wrap = doc.querySelector<HTMLElement>('.inspectorwrap');
  if (wrap) wrap.setAttribute('data-pinned', 'false');
}

/** Pin a feed row's redacted request/response. */
export function pinFeed(doc: Document, e: FeedRow): void {
  current = { kind: 'feed', seq: e.seq };
  const wrap = doc.querySelector<HTMLElement>('.inspectorwrap');
  if (wrap) wrap.setAttribute('data-pinned', 'true');
  const p = panel(doc);
  if (!p) return;
  const head = doc.createElement('div');
  head.className = 'inspector-head';
  head.append(span('inspector-desc', e.description), span('inspector-cid', e.correlationId));
  const pre = doc.createElement('pre');
  pre.className = 'inspector-pre';
  if (e.trace) {
    const t = e.trace;
    pre.textContent =
      `${t.method} ${t.url}\n\n` +
      `── request headers ──\n${pretty(t.requestHeaders)}\n\n` +
      `── request body ──\n${pretty(t.requestBody)}\n\n` +
      `── response (${t.responseStatus ?? 'no status'}) ──\n${pretty(t.responseBody)}`;
  } else {
    pre.textContent = `No captured HTTP exchange (simulated request).\n\ndetail: ${e.detail}`;
  }
  p.replaceChildren(head, pre);
}

/** Pin a multi-step flow's step breakdown (refreshes in place as it advances). */
export function pinFlow(doc: Document, f: FlowRef): void {
  current = { kind: 'flow', id: f.id };
  const wrap = doc.querySelector<HTMLElement>('.inspectorwrap');
  if (wrap) wrap.setAttribute('data-pinned', 'true');
  renderFlow(doc, f);
}

function renderFlow(doc: Document, f: FlowRef): void {
  const p = panel(doc);
  if (!p) return;
  const head = doc.createElement('div');
  head.className = 'inspector-head';
  head.append(span('inspector-desc', f.title), span('inspector-cid', `flow ${f.kind} · ${f.status}`));
  const pre = doc.createElement('pre');
  pre.className = 'inspector-pre';
  pre.textContent =
    f.steps
      .map((s, i) => `${STEP_GLYPH[s.status] ?? '○'} ${i + 1}. ${s.name}${s.msgType ? ` [${s.msgType}]` : ''}${s.detail ? `  — ${s.detail}` : ''}  [${s.status}]`)
      .join('\n') + `\n\n(modelled scheme lifecycle — the real one-shot calls are in the feed)`;
  p.replaceChildren(head, pre);
}

/** On each flows poll: live-update the pinned flow, or note it finished + scrolled off. */
export function refreshFlows(doc: Document, flows: FlowRef[]): void {
  if (current?.kind !== 'flow') return;
  const id = current.id;
  const f = flows.find((x) => x.id === id);
  if (f) renderFlow(doc, f);
}
